import { Cog6ToothIcon, TrashIcon } from '@heroicons/react/24/outline';
import { IconButton } from '@main/complykit';
import { Integration_Auth_Names_Enum, Integration_Names_Enum } from '@main/graphql/types.generated';
import { useTranslation } from 'react-i18next';
import z from 'zod/v4';

import {
  ConnectorConfigCollapse,
  type ConnectorConfigPersistence,
  useConnectedConnectorConfigEditor,
} from '../connector-config-collapse';
import { hasConfigurableAuthSchemas } from './app-connector-card-config';
import { AppConnectorCardFrame } from './app-connector-card-frame';

export type ConnectedAppConnectorEditableConfig = {
  configSchema: z.ZodObject;
  encryptedConfigSchema: z.ZodObject;
  persistence: ConnectorConfigPersistence;
};

type ConnectedAppConnectorCardBaseProps = {
  providerName: Integration_Names_Enum;
  connectorName?: string;
  authName: Integration_Auth_Names_Enum;
  controlsCount: number;
  evidenceCount: number;
  onDisconnect?: () => void;
};

export type ConnectedAppConnectorCardProps = ConnectedAppConnectorCardBaseProps & {
  editableConfig?: ConnectedAppConnectorEditableConfig;
};

export const ConnectedAppConnectorCard = ({
  providerName,
  connectorName,
  authName,
  controlsCount,
  evidenceCount,
  editableConfig,
  onDisconnect,
}: ConnectedAppConnectorCardProps) => {
  const { t } = useTranslation('integrations');
  const isConfigurable = Boolean(
    editableConfig &&
      hasConfigurableAuthSchemas({
        configSchema: editableConfig.configSchema,
        encryptedConfigSchema: editableConfig.encryptedConfigSchema,
      }),
  );

  if (isConfigurable && editableConfig) {
    return (
      <EditableConnectedAppConnectorCard
        providerName={providerName}
        connectorName={connectorName}
        authName={authName}
        controlsCount={controlsCount}
        evidenceCount={evidenceCount}
        editableConfig={editableConfig}
        onDisconnect={onDisconnect}
      />
    );
  }

  return (
    <AppConnectorCardFrame
      providerName={providerName}
      connectorName={connectorName}
      helpMessage={t('usageStatsMessage', { controlsCount, evidenceCount })}
      actions={onDisconnect ? <DisconnectButton onDisconnect={onDisconnect} /> : undefined}
    />
  );
};

type EditableConnectedAppConnectorCardProps = ConnectedAppConnectorCardBaseProps & {
  editableConfig: ConnectedAppConnectorEditableConfig;
};

const EditableConnectedAppConnectorCard = ({
  providerName,
  connectorName,
  authName,
  controlsCount,
  evidenceCount,
  editableConfig,
  onDisconnect,
}: EditableConnectedAppConnectorCardProps) => {
  const { t } = useTranslation('integrations');
  const configEditor = useConnectedConnectorConfigEditor(editableConfig.persistence);

  return (
    <AppConnectorCardFrame
      providerName={providerName}
      connectorName={connectorName}
      helpMessage={t('usageStatsMessage', { controlsCount, evidenceCount })}
      actions={
        <>
          <IconButton
            label={t('configureForm.configureBtn')}
            icon={Cog6ToothIcon}
            variant="soft"
            isPending={configEditor.isLoading}
            onPress={() => void configEditor.toggle()}
          />
          {onDisconnect && <DisconnectButton onDisconnect={onDisconnect} />}
        </>
      }
    >
      <ConnectorConfigCollapse
        editor={configEditor}
        t={t}
        tKey={`integrations.${providerName}.connectors.${authName}.config`}
        configSchema={editableConfig.configSchema}
        encryptedConfigSchema={editableConfig.encryptedConfigSchema}
        submitLabel={t('configureForm.saveBtn')}
      />
    </AppConnectorCardFrame>
  );
};

const DisconnectButton = ({ onDisconnect }: { onDisconnect: () => void }) => {
  const { t } = useTranslation('integrations');
  return (
    <IconButton
      variant="outline"
      tone="critical"
      label={t('disconnectBtn')}
      icon={TrashIcon}
      onPress={onDisconnect}
    />
  );
};
